/**
 * Configure wizard — Step 2: filters, live availability, and Generate.
 *
 * Debounced (500ms) availability check per purchased price-point via
 * checkLeadOrderAvailability; Generate is blocked while any file is short or
 * has an active fill-guarantee prompt. On submit, submitLeadOrderConfig flips
 * the order paid -> generating (the parent snapshot swaps the view).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useCredits } from '../../hooks/useCredits';
import { StateSelect } from './StateSelect';
import { buildRetailFilename, LeadOrderItem } from './useLeadOrder';

// Internal sector values are the engine's — only the labels are buyer-facing
// (James 2026-07-18: Oil & Gas / Precious Metals & Coins / Stocks & Private
// Placements renames).
const SECTOR_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'Oil', label: 'Oil & Gas' },
  { value: 'Gold', label: 'Precious Metals & Coins' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Crypto', label: 'Crypto' },
  { value: 'Stock', label: 'Stocks & Private Placements' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Tech', label: 'Tech' },
];

const sectorLabel = (v: string) => SECTOR_OPTIONS.find((s) => s.value === v)?.label || v;

export interface LeadOrderConfig {
  accredited: boolean;
  gender: 'both' | 'men' | 'women';
  white_males: boolean;
  email_required: boolean;
  cell_required: boolean;
  sectors: string[];
  /** null = All States */
  states: string[] | null;
}

interface AvailabilityResult {
  fillable?: boolean;
  available?: Record<string, number>;
  applied_relaxations?: Array<string | { step?: string; detail?: string }>;
  prompt?: { kind?: string; message?: string } | null;
  quality_ratio?: number;
}

interface StepFiltersProps {
  orderId: string;
  items: LeadOrderItem[];
  deliveryEmail: string;
  onBack: () => void;
}

function relaxLine(r: string | { step?: string; detail?: string }): string {
  if (typeof r === 'string') return r;
  return [r.step, r.detail].filter(Boolean).join(': ');
}

export function StepFilters({ orderId, items, deliveryEmail, onBack }: StepFiltersProps) {
  const { platformActive, activeBalance } = useCredits();

  // Filter state — EVERY box unchecked by default (James 2026-07-18): the
  // buyer explicitly checks each filter they want applied. Neither gender box
  // checked = no gender restriction.
  const [accredited, setAccredited] = useState(false);
  const [includeMen, setIncludeMen] = useState(false);
  const [includeWomen, setIncludeWomen] = useState(false);
  const [whiteMalesOnly, setWhiteMalesOnly] = useState(false);
  const [mustHaveEmail, setMustHaveEmail] = useState(false);
  const [cellPhoneOnly, setCellPhoneOnly] = useState(false);
  const [allActiveInvestors, setAllActiveInvestors] = useState(false);
  const [sectors, setSectors] = useState<string[]>([]);
  // undefined = no states choice made yet (availability + Generate held).
  const [states, setStates] = useState<string[] | null | undefined>(undefined);

  // Availability state, keyed by item index.
  const [availabilities, setAvailabilities] = useState<Record<number, AvailabilityResult>>({});
  const [checking, setChecking] = useState(false);
  const [availError, setAvailError] = useState<string | null>(null);

  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // A usable states choice: All States, or Select States with ≥1 picked.
  const statesChosen = states === null || (Array.isArray(states) && states.length > 0);

  const config = useMemo<LeadOrderConfig>(() => {
    const gender: 'both' | 'men' | 'women' =
      includeMen === includeWomen ? 'both' : includeMen ? 'men' : 'women';
    return {
      accredited,
      gender,
      white_males: whiteMalesOnly,
      email_required: mustHaveEmail,
      cell_required: cellPhoneOnly,
      sectors,
      states: states === undefined ? null : states,
    };
  }, [accredited, includeMen, includeWomen, whiteMalesOnly, mustHaveEmail, cellPhoneOnly, sectors, states]);

  // Serialized key so the debounce re-runs on any config / email change.
  const configKey = useMemo(
    () => JSON.stringify({ config, deliveryEmail }),
    [config, deliveryEmail]
  );

  const reqIdRef = useRef(0);
  // Single-flight guard: NEVER more than one availability request in flight
  // per wizard. Rapid filter toggling used to stack 40-80s engine calls
  // (superseded responses were ignored client-side, but each stale request
  // still occupied an engine instance → 429 "Rate exceeded", canary
  // 2026-07-18 evening). If the filters change mid-check we note it and
  // automatically re-check with the LATEST filters when the current one
  // finishes.
  const checkInFlightRef = useRef(false);
  const recheckQueuedRef = useRef(false);
  const [recheckTick, setRecheckTick] = useState(0);

  // Debounced availability check (500ms) — ONE batched call for all files.
  useEffect(() => {
    if (!statesChosen) {
      // No states choice yet — hold checks until the buyer picks.
      setAvailabilities({});
      setChecking(false);
      return;
    }

    const handle = setTimeout(() => {
      if (checkInFlightRef.current) {
        // A check is running — remember to re-run with the latest filters.
        recheckQueuedRef.current = true;
        setChecking(true);
        return;
      }

      const myId = ++reqIdRef.current;
      checkInFlightRef.current = true;
      setChecking(true);
      setAvailError(null);
      const fn = httpsCallable(functions, 'checkLeadOrderAvailability', { timeout: 240000 });
      const body = {
        orderId,
        items: items.map((it) => ({ pricePoint: it.pricePoint, qty: it.qty })),
        deliveryEmail,
        config,
      };

      const finish = () => {
        checkInFlightRef.current = false;
        if (recheckQueuedRef.current) {
          recheckQueuedRef.current = false;
          setRecheckTick((t) => t + 1);   // re-fires this effect with current filters
        }
      };

      const attempt = (isRetry: boolean) => {
        fn(body)
          .then((res) => {
            if (reqIdRef.current === myId) {
              const data = res.data as { results?: AvailabilityResult[] };
              const map: Record<number, AvailabilityResult> = {};
              (data.results || []).forEach((r, idx) => { map[idx] = r; });
              setAvailabilities(map);
              setChecking(false);
              setAvailError(null);
            }
            finish();
          })
          .catch((err) => {
            console.error('checkLeadOrderAvailability failed:', err);
            if (!isRetry) {
              // One automatic retry — most failures are transient load.
              if (reqIdRef.current === myId) {
                setAvailError('The availability check is taking longer than usual — automatically retrying…');
              }
              setTimeout(() => attempt(true), 2500);
              return;
            }
            if (reqIdRef.current === myId) {
              // Keep any previous results on screen; explain like the top note.
              setAvailError(
                'We couldn’t finish checking availability for this filter combination. ' +
                'Change any filter box (or re-tick one) to run a fresh check — leads can only ' +
                'be generated once every file above shows Fillable.',
              );
              setChecking(false);
            }
            finish();
          });
      };
      attempt(false);
    }, 500);

    return () => clearTimeout(handle);
    // configKey captures config + deliveryEmail; items/orderId are stable per order.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, statesChosen, recheckTick]);

  const anyPrompt = items.some((_, idx) => !!availabilities[idx]?.prompt);
  const anyUnfillable = items.some((_, idx) => {
    const a = availabilities[idx];
    return !a || a.fillable === false;
  });
  const generateDisabled =
    !statesChosen || checking || anyPrompt || anyUnfillable || generating || !!availError;

  const toggleSector = (v: string) => {
    setAllActiveInvestors(false);
    setSectors((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  const toggleAllActiveInvestors = () => {
    setAllActiveInvestors((prev) => {
      const next = !prev;
      if (next) setSectors([]);   // exclusive with specific sectors
      return next;
    });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const fn = httpsCallable(functions, 'submitLeadOrderConfig');
      await fn({ orderId, deliveryEmail, config });
      // Parent's snapshot flips status paid -> generating and swaps the view.
    } catch (err) {
      console.error('submitLeadOrderConfig failed:', err);
      setGenError(err instanceof Error ? err.message : 'Could not start generation. Please try again.');
      setGenerating(false);
    }
  };

  const showBillingNote = !platformActive || activeBalance <= 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ margin: 0, color: 'var(--gold)' }}>Configure Your Leads</h2>
        <button onClick={onBack} className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: '0.8rem' }}>
          ← Email
        </button>
      </div>

      <div className="buyleads-note" style={{ marginBottom: '14px' }}>
        <strong>How this works:</strong> check the box for every filter you want applied —
        unchecked options are NOT applied. Your leads are generated to match exactly the
        boxes you check, so review your selections before generating.
      </div>

      {showBillingNote && (
        <div className="buyleads-note">
          Heads up: your leads will be loaded into Run 1, but making calls requires an active platform fee and call credits.
        </div>
      )}

      {/* Per-price-point files (read-only totals + retail file-name preview + availability) */}
      <div className="buyleads-card">
        <h3 style={{ margin: '0 0 12px', color: 'var(--white)', fontSize: '1rem' }}>
          Your Order
        </h3>
        {checking && (
          <div className="buyleads-checking">
            <div className="buyleads-checking-bar" />
            <span>Checking availability for your filters…</span>
          </div>
        )}
        {items.length === 0 && (
          <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>No purchased items found on this order.</p>
        )}
        {items.map((it, idx) => {
          const a = availabilities[idx];
          const fileName = buildRetailFilename(it.qty, it.pricePoint);
          const unfillable = !!a && a.fillable === false;
          return (
            <div key={idx} style={{ marginBottom: '10px' }}>
              <div className="buyleads-file-row">
                <div>
                  <div style={{ color: 'var(--white)', fontWeight: 600 }}>
                    {it.qty.toLocaleString('en-US')} × {it.pricePoint} Leads
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.78rem', marginTop: '2px', fontFamily: 'monospace' }}>
                    {fileName}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {a ? (
                    !unfillable ? (
                      <span style={{ color: 'var(--green)', fontSize: '0.82rem', fontWeight: 600 }}>Fillable</span>
                    ) : (
                      <span style={{ color: 'var(--red, #ef4444)', fontSize: '0.82rem', fontWeight: 600 }}>Not fillable</span>
                    )
                  ) : (
                    <span style={{ color: 'var(--gray-500)', fontSize: '0.82rem' }}>{checking ? '…' : '—'}</span>
                  )}
                </div>
              </div>

              {a?.prompt?.message && (
                <div className="buyleads-prompt">{a.prompt.message}</div>
              )}

              {/* Unfillable with no specific engine prompt: tell the buyer which
                  of THEIR active filters to loosen. */}
              {unfillable && !a?.prompt?.message && (
                <div className="buyleads-prompt">
                  Not enough leads match the current filters. Try adjusting:{' '}
                  {[
                    Array.isArray(states) && 'add more states',
                    cellPhoneOnly && 'remove Cell Phone Only',
                    mustHaveEmail && 'remove Must Have Email',
                    whiteMalesOnly && 'remove White Males Only',
                    sectors.length > 0 && 'remove a sector',
                    !includeMen !== !includeWomen && 'include both men and women',
                    'or reduce the quantity',
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  .
                </div>
              )}

              {a?.applied_relaxations && a.applied_relaxations.length > 0 && (
                <div className="buyleads-relax">
                  {a.applied_relaxations.map((r, i) => (
                    <div key={i}>Adjusted: {relaxLine(r)}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="buyleads-card">
        <h3 style={{ margin: '0 0 12px', color: 'var(--white)', fontSize: '1rem' }}>Filters</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '4px 16px' }}>
          <label className="buyleads-check">
            <input type="checkbox" checked={accredited} onChange={(e) => setAccredited(e.target.checked)} />
            Accredited Only
          </label>
          <label className="buyleads-check">
            <input type="checkbox" checked={includeMen} onChange={(e) => setIncludeMen(e.target.checked)} />
            Include Men
          </label>
          <label className="buyleads-check">
            <input type="checkbox" checked={includeWomen} onChange={(e) => setIncludeWomen(e.target.checked)} />
            Include Women
          </label>
          <label className="buyleads-check">
            <input type="checkbox" checked={whiteMalesOnly} onChange={(e) => setWhiteMalesOnly(e.target.checked)} />
            White Males Only
          </label>
          <label className="buyleads-check">
            <input type="checkbox" checked={mustHaveEmail} onChange={(e) => setMustHaveEmail(e.target.checked)} />
            Must Have Email
          </label>
          <label className="buyleads-check">
            <input type="checkbox" checked={cellPhoneOnly} onChange={(e) => setCellPhoneOnly(e.target.checked)} />
            Cell Phone Only
          </label>
        </div>
      </div>

      {/* Sectors */}
      <div className="buyleads-card">
        <h3 style={{ margin: '0 0 4px', color: 'var(--white)', fontSize: '1rem' }}>Sectors</h3>
        <p style={{ color: 'var(--gray-500)', fontSize: '0.78rem', margin: 0 }}>
          Optional. Select one or more; leads are split evenly across chosen sectors.
        </p>
        <div className="buyleads-sector-grid">
          <label className="buyleads-check" style={{ fontWeight: 600 }}>
            <input type="checkbox" checked={allActiveInvestors} onChange={toggleAllActiveInvestors} />
            All Active Investors
          </label>
          {SECTOR_OPTIONS.map((opt) => (
            <label key={opt.value} className="buyleads-check">
              <input type="checkbox" checked={sectors.includes(opt.value)} onChange={() => toggleSector(opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* States */}
      <div className="buyleads-card">
        <h3 style={{ margin: '0 0 8px', color: 'var(--white)', fontSize: '1rem' }}>States</h3>
        <StateSelect value={states} onChange={setStates} />
      </div>

      {availError && (
        <div className="buyleads-prompt" style={{ marginBottom: '12px' }}>{availError}</div>
      )}
      {genError && (
        <div className="error-message">{genError}</div>
      )}

      {/* Second availability indicator by the action buttons — the one at the
          top scrolls out of view on long filter pages. */}
      {checking && (
        <div className="buyleads-checking" style={{ justifyContent: 'flex-end' }}>
          <div className="buyleads-checking-bar" />
          <span>Checking availability for your filters…</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '32px' }}>
        <button onClick={onBack} className="btn btn-secondary" disabled={generating}>Back</button>
        <button onClick={() => setShowConfirm(true)} className="btn btn-blue" disabled={generateDisabled}>
          {generating ? 'Starting…' : 'Generate My Leads'}
        </button>
      </div>

      {/* Filter-summary confirmation (James 2026-07-18): the buyer sees exactly
          what will be applied and must confirm before generation starts. */}
      {showConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div className="buyleads-card" style={{ maxWidth: '480px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 10px', color: 'var(--gold)' }}>Confirm Your Lead Order</h3>
            <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '12px' }}>
              Please review — your leads are generated to match exactly these selections.
            </p>
            <div style={{ fontSize: '0.88rem', color: 'var(--gray-200)', lineHeight: 1.8 }}>
              <div><strong>Delivery email:</strong> {deliveryEmail}</div>
              <div><strong>Files:</strong> {items.map((it) => `${it.qty.toLocaleString('en-US')} × ${it.pricePoint}`).join(', ')}</div>
              <div><strong>States:</strong> {states === null ? 'All States' : (states || []).join(', ')}</div>
              <div>
                <strong>Sectors:</strong>{' '}
                {allActiveInvestors || sectors.length === 0
                  ? 'All Active Investors'
                  : sectors.map(sectorLabel).join(', ')}
              </div>
              <div>
                <strong>Filters applied:</strong>{' '}
                {[
                  accredited && 'Accredited Only',
                  includeMen !== includeWomen && (includeMen ? 'Men only' : 'Women only'),
                  whiteMalesOnly && 'White Males Only',
                  mustHaveEmail && 'Must Have Email',
                  cellPhoneOnly && 'Cell Phone Only',
                ].filter(Boolean).join(', ') || 'None — all active investors included'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button className="btn btn-secondary" onClick={() => setShowConfirm(false)} disabled={generating}>
                Go Back
              </button>
              <button
                className="btn btn-blue"
                disabled={generating}
                onClick={() => { setShowConfirm(false); handleGenerate(); }}
              >
                Confirm &amp; Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
